import type { TaskType } from 'api-validations/ag-concepts'
import { applicationTypes } from 'api-validations/stitch'
import {
  type AgronomicActivityReport,
  AgronomicReportToggle,
  LegendScaleKind,
  legendScaleSchema,
  CondensedReportToggle,
  type CropSelection,
  CropSelectionKind,
  cropSegmentCodec,
  getDefaultTogglesForReportType,
  getTogglesForReportType,
  type IncludedDataToggle,
  InputReportToggle,
  MERGEABLE_TASKS,
  type PassDataFilters,
  type ReportType
} from 'api-validations/tailor'
import { stringToInt, stringToNumber } from 'api-validations/zod-extra'
import { Enum, isNil, isNonNil, type NonEmptyArray } from 'utils'
import { z } from 'zod'

import { defaultPageSetup, type PageSetup, PaperOrientation, PaperSize } from './document/pageSetup'
import { initialFilters, type Season } from './reportingTypes'

export const findSeason = (
  year: string | null,
  cropSegment: string | null,
  seasons: Season[]
): Season | null => {
  if (isNil(year) || isNil(cropSegment)) return null

  const yearNum = stringToInt.decode(year)
  if (Number.isNaN(yearNum)) return null

  return (
    seasons.find((s) => s.year === yearNum && cropSegmentCodec.encode(s.crop) === cropSegment) ??
    null
  )
}

export const encodeFiltersToParam = (filters: Record<string, boolean>): string =>
  Object.entries(filters)
    .flatMap(([key, isSelected]) => (isSelected ? [key] : []))
    .join(',')

export const decodeFiltersFromParams = (
  searchParams: URLSearchParams,
  paramName: string
): PassDataFilters => {
  const selectedTasks = searchParams.getAll(paramName)

  if (selectedTasks.length === 0) {
    return Object.fromEntries(
      Object.keys(initialFilters).map((key) => [key, false])
    ) as PassDataFilters
  }

  return Object.fromEntries(
    Object.keys(initialFilters).map((key) => [key, selectedTasks.includes(key)])
  ) as PassDataFilters
}

export const decodeDeviceSerialsFromParams = (
  searchParams: URLSearchParams,
  paramName: string
): number[] =>
  searchParams.getAll(paramName).flatMap((value) => {
    const serial = stringToInt.decode(value)
    return Number.isNaN(serial) ? [] : [serial]
  })

/**
 * Page setup rides in the URL so a printout is reproducible: a link names the paper it was laid out
 * for, not just the data it covers.
 */
export const decodePageSetupFromParams = (searchParams: URLSearchParams): PageSetup => {
  const paper = searchParams.get('paper')
  const orientation = searchParams.get('orientation')

  return {
    paper: Enum.isValidValue(paper, PaperSize) ? paper : defaultPageSetup.paper,
    orientation: Enum.isValidValue(orientation, PaperOrientation)
      ? orientation
      : defaultPageSetup.orientation,
    onePagePerField: searchParams.get('pagePerField') === 'true'
  }
}

export const encodePageSetupToParams = (setup: PageSetup): Record<string, string | null> => ({
  paper: setup.paper === defaultPageSetup.paper ? null : setup.paper,
  orientation: setup.orientation === defaultPageSetup.orientation ? null : setup.orientation,
  pagePerField: setup.onePagePerField ? 'true' : null
})

/**
 * A layer travels as `applicationType:applicationId:layerKey`. None of the three contains a colon:
 * an application id is url-safe base64.
 */
const LAYER_SEPARATOR = ':'

export const encodeLayersToParams = (layers: AgronomicActivityReport.Layer[]): Set<string> =>
  new Set(
    layers.map(({ applicationType, applicationId, layerKey }) =>
      [applicationType, applicationId, layerKey].join(LAYER_SEPARATOR)
    )
  )

/**
 * The layers a URL names, skipping any that are malformed or whose application type is not one
 * stitch knows.
 */
export const decodeLayersFromParams = (
  searchParams: URLSearchParams,
  paramName: string
): AgronomicActivityReport.Layer[] =>
  searchParams.getAll(paramName).flatMap((value) => {
    const [type, applicationId, layerKey, ...extra] = value.split(LAYER_SEPARATOR)
    const applicationType = applicationTypes.safeParse(type)
    const isMissingPart =
      applicationId === undefined ||
      applicationId === '' ||
      layerKey === undefined ||
      layerKey === ''
    if (!applicationType.success || isMissingPart || extra.length > 0) return []
    return [{ applicationType: applicationType.data, applicationId, layerKey }]
  })

/**
 * A legend setting travels as its layer, then its scale: `perMap`, or `shared:minimum:maximum:bins`.
 */
export const encodeLegendsToParams = (
  settings: AgronomicActivityReport.LegendSetting[]
): Set<string> =>
  new Set(
    settings.map(({ applicationType, applicationId, layerKey, scale }) => {
      const scaleParts =
        scale.kind === LegendScaleKind.PerMap
          ? [scale.kind]
          : [scale.kind, scale.range.minimum, scale.range.maximum, scale.binCount]
      return [applicationType, applicationId, layerKey, ...scaleParts].join(LAYER_SEPARATOR)
    })
  )

/**
 * The scale of one legend setting's `perMap` or `shared:minimum:maximum:bins` tail.
 */
const scalePartsSchema = z
  .union([
    z.tuple([z.literal(LegendScaleKind.PerMap)]).transform(([kind]) => ({ kind })),
    z
      .tuple([z.literal(LegendScaleKind.Shared), stringToNumber, stringToNumber, stringToNumber])
      .transform(([kind, minimum, maximum, binCount]) => ({
        kind,
        range: { minimum, maximum },
        binCount
      }))
  ])
  .pipe(legendScaleSchema)

/**
 * The legend settings a URL names, skipping any it cannot read.
 */
export const decodeLegendsFromParams = (
  searchParams: URLSearchParams,
  paramName: string
): AgronomicActivityReport.LegendSetting[] =>
  searchParams.getAll(paramName).flatMap((value) => {
    const [type, applicationId, layerKey, ...scaleParts] = value.split(LAYER_SEPARATOR)
    const applicationType = applicationTypes.safeParse(type)
    const scale = scalePartsSchema.safeParse(scaleParts)
    const isMissingPart =
      applicationId === undefined ||
      applicationId === '' ||
      layerKey === undefined ||
      layerKey === ''
    if (!applicationType.success || isMissingPart || !scale.success) return []
    return [{ applicationType: applicationType.data, applicationId, layerKey, scale: scale.data }]
  })

const mergeableTaskSchema = z.enum(MERGEABLE_TASKS)

/**
 * The tasks a URL merges each field's passes of, skipping any it cannot read.
 */
export const decodeMergedTasksFromParams = (
  searchParams: URLSearchParams,
  paramName: string
): AgronomicActivityReport.MergeableTask[] =>
  searchParams.getAll(paramName).flatMap((value) => {
    const task = mergeableTaskSchema.safeParse(value)
    return task.success ? [task.data] : []
  })

export const decodeOptionalFiltersFromParams = (
  searchParams: URLSearchParams,
  paramName: string
): Set<IncludedDataToggle> => {
  const values = searchParams.getAll(paramName)

  const allValidToggles: IncludedDataToggle[] = [
    ...Object.values(InputReportToggle),
    ...Object.values(CondensedReportToggle),
    ...Object.values(AgronomicReportToggle)
  ]

  return new Set(
    values.filter((v): v is IncludedDataToggle => allValidToggles.includes(v as IncludedDataToggle))
  )
}

/**
 * The sections a requested report will show, and the requested ones it has no place for. Sections a
 * request names are added to the ones the report type already shows, so naming one does not drop the
 * rest.
 */
export const sectionsForReport = ({
  reportType,
  requested
}: {
  reportType: ReportType
  requested: IncludedDataToggle[]
}): { sections: IncludedDataToggle[]; unsupported: IncludedDataToggle[] } => {
  const honored = getTogglesForReportType(reportType)

  return {
    sections: [
      ...new Set([
        ...getDefaultTogglesForReportType(reportType),
        ...requested.filter((toggle) => honored.includes(toggle))
      ])
    ],
    unsupported: requested.filter((toggle) => !honored.includes(toggle))
  }
}

/**
 * The season a request names, as the selector understands it.
 *
 * A year that grew one crop has no all-crops entry, so a request naming no crop resolves to that
 * single crop rather than to nothing. Absent when the year holds no seasons at all, or when the crop
 * named is not one the year grew — a report over either would come out empty.
 */
export const resolveRequestedSeason = ({
  year,
  crop,
  seasons
}: {
  year: number
  crop: string | null
  seasons: Season[]
}): Season | undefined => {
  const grownThatYear = seasons.filter((season) => season.year === year)

  if (isNonNil(crop)) {
    return grownThatYear.find(
      (season) => season.crop.kind === CropSelectionKind.Single && season.crop.crop === crop
    )
  }

  const everyCrop = grownThatYear.find((season) => season.crop.kind === CropSelectionKind.All)
  const onlyCrop = grownThatYear.length === 1 ? grownThatYear[0] : undefined

  return everyCrop ?? onlyCrop
}

/**
 * The parameters one report is built from — which report, over what season and crop, narrowed to a
 * client, farm, field, date range, task types, and devices, plus the optional sections to include.
 *
 * A null list means the report type's own default rather than an empty selection. An absent param is
 * the only thing the URL can say, so a list that is present is non-empty.
 */
export type ReportRequest = {
  reportType: ReportType
  season: number
  crop: CropSelection
  client: string | null
  farm: string | null
  field: string | null
  dateRange: { start: string; end: string } | null
  tasks: NonEmptyArray<TaskType> | null
  deviceSerialNumbers: NonEmptyArray<number> | null
  includedData: NonEmptyArray<IncludedDataToggle> | null
}

/**
 * The reporting page's URL for a {@link ReportRequest} — the encode half of the decoders above.
 */
export const reportLink = (basePath: string, request: ReportRequest): string => {
  const params = new URLSearchParams()

  params.set('reportType', request.reportType)
  params.set('year', String(request.season))
  params.set('crop', cropSegmentCodec.encode(request.crop))

  if (isNonNil(request.client)) params.set('client', request.client)
  if (isNonNil(request.farm)) params.set('farm', request.farm)
  if (isNonNil(request.field)) params.set('field', request.field)

  if (isNonNil(request.dateRange)) {
    params.set('startDate', request.dateRange.start)
    params.set('endDate', request.dateRange.end)
  }

  for (const task of request.tasks ?? []) params.append('passFilters', task)
  for (const serial of request.deviceSerialNumbers ?? []) params.append('devices', String(serial))
  for (const toggle of request.includedData ?? []) params.append('optionalFilters', toggle)

  return `${basePath}?${params.toString()}`
}
